import * as z from 'zod';

import * as api from '@gitbook/api-client';
import { createOrderIndex, type OrderIndexPosition } from '@gitbook/util-orderindex';

import {
    type ContentOwnership,
    ContentSharingSchema,
    type DefaultPermissionLevel,
} from './content';
import { Timestamp } from './db/firestore';
import { genID } from './helpers';

export const COLLECTION_TITLE_LIMIT = 50;
export const COLLECTION_DESCRIPTION_LIMIT = 100;

export const COLLECTION_DEFAULT_TITLE = 'Untitled';

// Max-depth of nested content. 3 means content can be nested
// at the root level (1), first child (2), and second child (3).
export const COLLECTION_MAX_DEPTH = 3;

/**
 * OrderIndexes.
 */
export const OrderIndexSchema = z.string().min(1).max(512);

/**
 * A collection can be published using different modes.
 */
export const CollectionPublishingSchema = z.discriminatedUnion('publishingType', [
    z.object({
        publishingType: z.literal('variants'),
        primarySpace: z.string().optional(),
    }),
    z.object({
        publishingType: z.undefined().optional(),
        primarySpace: z.undefined().optional(),
    }),
]);
export type CollectionPublishing = z.infer<typeof CollectionPublishingSchema>;

/**
 * Collection of spaces and nested collections for an user or an organization.
 */
export const CollectionSchema = ContentSharingSchema.extend({
    key: z.string(),
    title: z.string().max(COLLECTION_TITLE_LIMIT),
    description: z.string().max(COLLECTION_DESCRIPTION_LIMIT).optional(),
    /**
     * Index to order the collection with others.
     */
    orderIndex: OrderIndexSchema,
    /**
     * When the collection was created
     */
    createdAt: z.instanceof(Timestamp),
    /**
     * The user who created the collection
     */
    createdBy: z.string(),
    /**
     * If this collection is deleted (or being deleted), the user who deleted it.
     */
    deletedBy: z.string().optional(),

    visibility: z.enum(api.ContentVisibility),
})
    .and(CollectionPublishingSchema)
    .refine(
        (collection) => {
            // Ensure that we can't nest a collection in itself
            if ('collection' in collection) {
                return collection.collection !== collection.key;
            }

            return true;
        },
        { message: 'A collection cannot reference itself as parent' }
    );
export type Collection = z.infer<typeof CollectionSchema>;

export interface CollectionCreateArgs {
    key?: string;
    title?: string;
    owner: ContentOwnership;
    at?: OrderIndexPosition;
    createdBy: string;
    defaultPermission?: DefaultPermissionLevel;

    /**
     * Parent of the to-be-created collection.
     */
    collection?: string;
}

/**
 * Create a new collection.
 */
export function createCollection(args: CollectionCreateArgs): Collection {
    const key = args.key || genID();

    const collection: Collection = {
        key,
        title: normalizeCollectionTitle(args.title || ''),
        orderIndex: createOrderIndex(args.at),
        defaultPermission:
            typeof args.defaultPermission === 'undefined' ? 'inherit' : args.defaultPermission,
        visibility: api.ContentVisibility.Private,
        createdAt: Timestamp.now(),
        createdBy: args.createdBy,
        ...(args.collection ? { collection: args.collection } : {}),
        ...args.owner,
    };

    return collection;
}

/**
 * Normalize a collection title.
 */
export function normalizeCollectionTitle(title: string): string {
    return title
        .replace(/(\r\n|\r|\n)/g, ' ')
        .trim()
        .slice(0, COLLECTION_TITLE_LIMIT)
        .trim();
}
